// FLAGS: silent-failure
// Edits an expert's status, session-type, language and gender tags plus their themes.
import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
  psychologistLanguageTagName,
  psychologistGenderTagName,
  psychologistStatusTagName,
  sessionTypeTagName,
  useUserTagsQuery,
  useMultipleTagsQueries,
} from "@src/queries/tags";
import { usePsychologistThemesQuery } from "@src/queries/experts";
import { useThemesQuery } from "@src/queries/themes";
import { DialogActionButtons, DialogHeader } from "./DialogToolkit";
import TagSelector from "./TagSelector";
import CustomCheckbox from "./form/CustomCheckbox";

interface UpdatePsychologistTagsDialogProps {
  client: {
    id: string;
    firstName: string;
    lastName: string;
  };
  refetchPsychologistsList?: () => void;
  open: boolean;
  onClose: () => void;
}

const UpdatePsychologistTagsDialog = ({
  client,
  refetchPsychologistsList = () => {},
  open,
  onClose,
}: UpdatePsychologistTagsDialogProps) => {
  const { t } = useTranslation();

  const { taggedData } = useMultipleTagsQueries([
    psychologistStatusTagName,
    sessionTypeTagName,
    psychologistLanguageTagName,
    psychologistGenderTagName,
  ]);
  const psychologistStatusTags = taggedData[psychologistStatusTagName];
  const sessionTypeTags = taggedData[sessionTypeTagName];
  const languageTags = taggedData[psychologistLanguageTagName];
  const genderTags = taggedData[psychologistGenderTagName];

  const { data: allThemes } = useThemesQuery(undefined);
  const { psychologistThemes, savePsychologistThemes } =
    usePsychologistThemesQuery(client.id);
  const {
    userTags: userPsychologistStatusTags,
    saveTags: savePsychologistStatusTags,
  } = useUserTagsQuery(client.id, psychologistStatusTagName);
  const { userTags: userSessionTypeTags, saveTags: saveSessionTypeTags } =
    useUserTagsQuery(client.id, sessionTypeTagName);
  const { userTags: userLanguageTags, saveTags: saveLanguageTags } =
    useUserTagsQuery(client.id, psychologistLanguageTagName);
  const { userTags: userGenderTag, saveTags: saveGenderTag } = useUserTagsQuery(
    client.id,
    psychologistGenderTagName,
  );

  const [psychologistStatus, setPsychologistStatus] = useState<string[]>([]);
  const [psychologistSessionTypes, setPsychologistSessionTypes] = useState<
    string[]
  >([]);
  const [psychologistLanguage, setPsychologistLanguage] = useState<string[]>(
    [],
  );
  const [psychologistGender, setPsychologistGender] = useState<string[]>([]);
  const [selectedPsychologistThemes, setSelectedPsychologistThemes] = useState<
    string[]
  >([]);
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    setPsychologistStatus(
      userPsychologistStatusTags?.map((tag) => tag.name ?? "") ?? [],
    );
    setPsychologistSessionTypes(
      userSessionTypeTags?.map((tag) => tag.name ?? "") ?? [],
    );
    setPsychologistLanguage(
      userLanguageTags?.map((tag) => tag.name ?? "") ?? [],
    );
    setPsychologistGender(userGenderTag?.map((tag) => tag.name ?? "") ?? []);
    setSelectedPsychologistThemes(
      psychologistThemes?.map((theme) => theme.key ?? "") ?? [],
    );
  }, [
    userPsychologistStatusTags,
    userSessionTypeTags,
    userLanguageTags,
    userGenderTag,
    psychologistThemes,
  ]);

  const formik = useFormik({
    initialValues: {
      psychologistState: psychologistStatus,
      psychologistGender,
    },
    // FLAGS: silent-failure
    // Five independent saves run in one Promise.all with no catch: if any rejects the others have
    // already been applied, the dialog stays open and nothing is reported.
    onSubmit: async () => {
      Promise.all([
        saveLanguageTags(psychologistLanguage),
        saveGenderTag(psychologistGender),
        savePsychologistStatusTags(psychologistStatus),
        saveSessionTypeTags(psychologistSessionTypes),
        savePsychologistThemes(selectedPsychologistThemes),
      ]).then(() => {
        onClose();
        refetchPsychologistsList();
      });
    },
    enableReinitialize: true,
  });

  const onChangeTheme = (theme: string) => {
    if (selectedPsychologistThemes.includes(theme)) {
      setSelectedPsychologistThemes(
        selectedPsychologistThemes.filter(
          (selectedPsychologistTheme) => selectedPsychologistTheme !== theme,
        ),
      );
    } else {
      setSelectedPsychologistThemes(selectedPsychologistThemes.concat(theme));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] overflow-y-auto">
      <button
        type="button"
        className="fixed inset-0 h-full w-full cursor-default border-none bg-black/50"
        aria-label="Close dialog"
        onClick={() => {
          formik.resetForm();
          onClose();
        }}
      />
      <div className="flex min-h-full items-center justify-center p-8">
        <div className="relative w-full max-w-[600px] rounded bg-white p-6 shadow-2xl">
          <DialogHeader
            iconLigature="psychology"
            title={`${client.firstName}, ${client.lastName}`}
            outlined
          />
          <form
            onSubmit={formik.handleSubmit}
            className="flex min-h-[500px] flex-col flex-wrap overflow-x-hidden overflow-y-auto"
            noValidate
          >
            <div className="grid grid-cols-2 gap-0">
              <div>
                <TagSelector
                  tagTypeName={t("Tags.PsychologistStatusTagsTitle")}
                  tagState={psychologistStatus}
                  setTagState={setPsychologistStatus}
                  tagType={psychologistStatusTags}
                  singleChoice
                />
              </div>
              <div>
                <TagSelector
                  tagTypeName={t("Tags.SessionTypeTagsTitle")}
                  tagState={psychologistSessionTypes}
                  setTagState={setPsychologistSessionTypes}
                  tagType={sessionTypeTags}
                />
                <TagSelector
                  tagTypeName={t("Common.Gender")}
                  tagState={psychologistGender}
                  setTagState={setPsychologistGender}
                  tagType={genderTags}
                  singleChoice
                  // @ts-expect-error TagSelector is untyped JSX — props typed from defaults
                  formik={formik}
                  // @ts-expect-error TagSelector is untyped JSX — props typed from defaults
                  formikValue="psychologistGender"
                />
              </div>
              <div className="col-span-2">
                <TagSelector
                  tagTypeName={t("Tags.LanguageTagsTitle")}
                  tagState={psychologistLanguage}
                  setTagState={setPsychologistLanguage}
                  tagType={languageTags}
                />
              </div>
              <div className="col-span-2">
                <span className="text-xs text-[#48B4FF]">
                  {t("SharedStrings.Themes")}
                </span>
                <div className="grid grid-cols-2">
                  <div className="flex flex-col">
                    {allThemes
                      .slice(0, Math.ceil(allThemes.length / 2))
                      .filter(
                        (theme): theme is typeof theme & { key: string } =>
                          theme.key != null,
                      )
                      .map((theme) => (
                        <CustomCheckbox
                          t={t}
                          key={theme.key}
                          value={theme.key}
                          // @ts-expect-error CustomCheckbox is untyped JSX — onCheck typed from default
                          onCheck={onChangeTheme}
                          isChecked={selectedPsychologistThemes.includes(
                            theme.key,
                          )}
                        />
                      ))}
                  </div>
                  <div className="flex flex-col">
                    {allThemes
                      .slice(Math.ceil(allThemes.length / 2))
                      .filter(
                        (theme): theme is typeof theme & { key: string } =>
                          theme.key != null,
                      )
                      .map((theme) => (
                        <CustomCheckbox
                          t={t}
                          key={theme.key}
                          value={theme.key}
                          // @ts-expect-error CustomCheckbox is untyped JSX — onCheck typed from default
                          onCheck={onChangeTheme}
                          isChecked={selectedPsychologistThemes.includes(
                            theme.key,
                          )}
                        />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </form>
          <DialogActionButtons
            onCancel={() => {
              formik.resetForm();
              onClose();
            }}
            onSubmit={formik.submitForm}
          />
        </div>
      </div>
    </div>
  );
};

export default UpdatePsychologistTagsDialog;
